import {
  Box,
  BoxProps,
  ButtonProps,
  DarkMode,
  InputProps,
  LightMode,
  SpinnerProps,
  TextProps
} from "@chakra-ui/react"
import classNames from "classnames"

type BGModeProps = {
  mode?: "dark" | "light"
  exclusions?: string[]
  text?: TextProps
  input?: InputProps
  button?: ButtonProps
  spinner?: SpinnerProps
} & BoxProps

function BGMode({
  mode = "dark",
  exclusions,
  text,
  input,
  button,
  spinner,
  ...props
}: BGModeProps) {
  const ColorModeComponent = mode === "dark" ? DarkMode : LightMode

  const textStyles = {
    color: mode === "dark" ? "white" : undefined,
    ...text
  }

  const inputStyles: InputProps = {
    bg: "white !important",
    borderRadius: "20px",
    _placeholder: { color: "gray.500" },
    ...input
  }

  const inputPlaceholderStyles = {
    _placeholder: { color: "gray.500" },
    ...input?._placeholder
  }

  const buttonStyles: ButtonProps = {
    ...button,
    _disabled: {
      color: "whiteAlpha.500",
      opacity: 0.6,
      cursor: "not-allowed",
      ...button?._disabled
    }
  }

  const spinnerStyles: SpinnerProps = {
    borderTopColor: "white",
    borderRightColor: "white",
    ...spinner
  }

  let sx: NonNullable<BoxProps["sx"]> = {
    ...props.sx,
    // NOTE: using classnames instead of tag names since, at least with "p",
    // when using tag names it seems to affect other styling.

    // textStyles
    [`[data-theme="${mode}"].chakra-heading`]: textStyles,
    [`[data-theme="${mode}"].chakra-text`]: textStyles,
    [`[data-theme="${mode}"].chakra-form__label`]: textStyles,
    [`[data-theme="${mode}"].chakra-form__error-message`]: textStyles,
    [`[data-theme="${mode}"].chakra-input__right-addon`]: textStyles,
    [`[data-theme="${mode}"].chakra-checkbox__label`]: textStyles,
    [`[data-theme="${mode}"].chakra-icon`]: textStyles,

    // inputStyles
    [`[data-theme="${mode}"].chakra-input`]: inputStyles,
    [`input[data-theme="${mode}"][id^="field-:r"]`]: inputStyles,
    [`input[data-theme="${mode}"]:-internal-autofill-previewed`]: inputStyles,
    [`input[data-theme="${mode}"]:-internal-autofill-selected`]: inputStyles,

    // inputPlaceholderStyles
    // Found in chakra-react-select.
    [`[data-theme="${mode}"][id$="-placeholder"]`]: inputPlaceholderStyles,

    // buttonStyles
    [`button[data-theme="${mode}"].chakra-button`]: buttonStyles,

    // Enabled button's icon styles.
    [`button[data-theme="${mode}"].chakra-button:not(:disabled) [data-theme="${mode}"].chakra-button__icon,
      button[data-theme="${mode}"].chakra-button:not(:disabled) [data-theme="${mode}"].chakra-button__icon [data-theme="${mode}"].chakra-icon`]:
      {
        color: mode === "dark" ? "gray.800" : undefined
      },

    // Disabled button's icon styles.
    [`button[data-theme="${mode}"].chakra-button:disabled [data-theme="${mode}"].chakra-button__icon,
        button[data-theme="${mode}"].chakra-button:disabled [data-theme="${mode}"].chakra-button__icon [data-theme="${mode}"].chakra-icon`]:
      {
        color: buttonStyles?._disabled?.color || "whiteAlpha.500"
      },
    [`[data-theme="${mode}"].chakra-spinner`]: spinnerStyles
  }

  // Exclude the override styles from these selectors and their descendents.
  if (exclusions) {
    sx = {
      [`*${exclusions.map((selector) => `:not(${selector} *)`).join("")}`]: sx
    }
  }

  return (
    <ColorModeComponent>
      <Box
        {...props}
        className={classNames("chakra-ui-reset-css", props.className)}
        sx={sx}
      />
    </ColorModeComponent>
  )
}

export default BGMode
